/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Sidebar navigation component. Provides main menu navigation and
quick access to key application features and sections.
*/

import { Home, Bike, Users, BarChart3, Settings, Menu, Wrench, Map, Calendar, CreditCard, Leaf, Bus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar as UISidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
} from "@/components/ui/sidebar";

export function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();

  const menuItems = [
    {
      title: "Dashboard",
      icon: Home,
      path: "/",
    },
    {
      title: "eBikes",
      icon: Bike,
      path: "/bikes",
    },
    {
      title: "Bookings",
      icon: Calendar,
      path: "/bookings",
    },
    {
      title: "Bike Bus",
      icon: Bus,
      path: "/bike-bus",
    },
    {
      title: "Users",
      icon: Users,
      path: "/users",
    },
    {
      title: "Statistics",
      icon: BarChart3,
      path: "/statistics",
    },
    {
      title: "Emissions",
      icon: Leaf,
      path: "/emissions",
    },
    // Only show maintenance, locations, and transactions tabs for admin users
    ...(user?.role === "admin" ? [
      {
        title: "Maintenance",
        icon: Wrench,
        path: "/maintenance",
      },
      {
        title: "Locations",
        icon: Map,
        path: "/locations",
      },
      {
        title: "Transactions",
        icon: CreditCard,
        path: "/transactions",
      }
    ] : []),
    {
      title: "Settings",
      icon: Settings,
      path: "/settings",
    },
  ];
  
  return (
    <UISidebar>
      <div className="flex h-14 items-center border-b px-4">
        <Link 
          to="/" 
          className="flex items-center gap-2 font-semibold"
        >
          <img 
            src="/lovable-uploads/7f9a3b72-1a60-4ef2-b566-286979847f68.png" 
            alt="Garage" 
            className="h-8 w-auto"
          />
        </Link>
        <div className="ml-auto md:hidden">
          <SidebarTrigger>
            <Menu className="h-5 w-5" />
          </SidebarTrigger>
        </div>
      </div>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Main Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton asChild>
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        location.pathname === item.path && "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </UISidebar>
  );
}
